import GDPRManager from './GDPRManager'

let mixpanelModulePromise = null

function loadMixpanelModule() {
    if (!mixpanelModulePromise) {
        mixpanelModulePromise = import(/* webpackChunkName: "analytics-mixpanel" */ 'mixpanel-browser')
            .then(module => module?.default || module)
            .catch((error) => {
                mixpanelModulePromise = null
                throw error
            })
    }

    return mixpanelModulePromise
}

export default class MixpanelClientService {
    constructor() {
        this.enabled = false
        this.initialized = false
        this.gdprManager = null
        this.mixpanelApi = null
        this.loadingPromise = null
        
        // Initialize from global config if available
        if (window.mixpanelConfig) {
            this.initialize(window.mixpanelConfig)
        }
    }

    /**
     * Initialize Mixpanel with configuration and GDPR compliance
     * @param {Object} config - Mixpanel configuration
     */
    async initialize(config) {
        if (this.initialized) {
            return
        }

        if (this.loadingPromise) {
            return this.loadingPromise
        }

        this.loadingPromise = (async () => {
            try {
                // Initialize GDPR manager
                this.gdprManager = GDPRManager.getInstance()

                // Check for analytics consent
                if (!this.gdprManager.canCollectAnalytics()) {
                    console.log('[Mixpanel] Analytics disabled - no user consent')
                    this.enabled = false
                    return
                }

                if (config.enabled && config.projectToken) {
                    const mixpanel = await loadMixpanelModule()

                    this.mixpanelApi = mixpanel
                    mixpanel.init(config.projectToken, {
                        debug: config.debug || false,
                        track_pageview: false,
                        persistence: 'localStorage',
                        api_host: 'https://api.mixpanel.com',
                        cross_subdomain_cookie: false,
                        secure_cookie: true,
                        ip: false, // Don't track IP on client side
                        ignore_dnt: false, // Respect "Do Not Track" browser setting
                        property_blacklist: ['$current_url', '$referrer', '$referring_domain', '$ip'],
                        opt_out_tracking_by_default: false,
                        batch_requests: true
                    })
                    
                    this.enabled = true
                    this.initialized = true
                    console.log('[Mixpanel] Analytics initialized with user consent')
                } else {
                    console.log('[Mixpanel] Service disabled or missing project token')
                }
            } catch (error) {
                console.error('[Mixpanel Client] Failed to initialize:', error)
                this.enabled = false
            }
        })()

        try {
            await this.loadingPromise
        } finally {
            this.loadingPromise = null
        }
    }

    /**
     * Check if tracking is allowed
     */
    canTrack() {
        return this.initialized && this.enabled &&
            this.mixpanelApi &&
            this.gdprManager && this.gdprManager.canCollectAnalytics()
    }

    /**
     * Disable analytics tracking
     */
    disable() {
        if (this.initialized && this.mixpanelApi) {
            try {
                this.mixpanelApi.opt_out_tracking?.()
            } catch (error) {
                console.error('[Mixpanel] Error opting out:', error)
            }
        }
        this.enabled = false
        console.log('[Mixpanel] Analytics tracking disabled')
    }

    /**
     * Enable analytics tracking (if consent given)
     */
    enable() {
        if (this.gdprManager && this.gdprManager.canCollectAnalytics()) {
            if (this.initialized && this.mixpanelApi) {
                try {
                    this.mixpanelApi.opt_in_tracking?.()
                } catch (error) {
                    console.error('[Mixpanel] Error opting in:', error)
                }
            }
            this.enabled = true
            console.log('[Mixpanel] Analytics tracking enabled')
        }
    }

    /**
     * Track a client-side event
     * @param {string} eventName - The event name
     * @param {Object} properties - Event properties
     */
    track(eventName, properties = {}) {
        if (!this.canTrack()) return

        try {
            const eventProperties = {
                ...properties,
                timestamp: new Date().toISOString(),
                client_side: true,
                user_agent: navigator.userAgent,
                screen_resolution: `${screen.width}x${screen.height}`,
                viewport_size: `${window.innerWidth}x${window.innerHeight}`,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            }

            if (this.mixpanelApi?.track) {
                this.mixpanelApi.track(eventName, eventProperties)
            }
        } catch (error) {
            console.error('[Mixpanel Client] Error tracking event:', error)
        }
    }

    /**
     * Track game events
     * @param {string} action - The game action
     * @param {Object} data - Event data
     */
    trackGameEvent(action, data = {}) {
        if (!this.canTrack()) return

        const gameData = {
            ...data,
            game_action: action,
            scene: this.getCurrentScene(),
            timestamp: new Date().toISOString()
        }

        this.track(`Game: ${action}`, gameData)
    }

    /**
     * Track UI interactions
     * @param {string} element - The UI element
     * @param {string} action - The action (click, hover, etc.)
     * @param {Object} data - Additional data
     */
    trackUIInteraction(element, action, data = {}) {
        if (!this.enabled) return

        this.track('UI Interaction', {
            ui_element: element,
            ui_action: action,
            ...data
        })
    }

    /**
     * Track scene changes
     * @param {string} fromScene - Previous scene
     * @param {string} toScene - New scene
     * @param {Object} data - Additional data
     */
    trackSceneChange(fromScene, toScene, data = {}) {
        if (!this.enabled) return

        this.track('Scene Change', {
            from_scene: fromScene,
            to_scene: toScene,
            ...data
        })
    }

    /**
     * Track game performance metrics
     * @param {Object} metrics - Performance metrics
     */
    trackPerformance(metrics = {}) {
        if (!this.enabled) return

        this.track('Performance Metrics', {
            fps: metrics.fps,
            memory_used: performance.memory ? performance.memory.usedJSHeapSize : null,
            memory_total: performance.memory ? performance.memory.totalJSHeapSize : null,
            load_time: metrics.loadTime,
            scene: this.getCurrentScene(),
            ...metrics
        })
    }

    /**
     * Track errors
     * @param {Error} error - The error object
     * @param {Object} context - Error context
     */
    trackError(error, context = {}) {
        if (!this.enabled) return

        this.track('Client Error', {
            error_message: error.message,
            error_stack: error.stack,
            error_name: error.name,
            scene: this.getCurrentScene(),
            ...context
        })
    }

    /**
     * Track minigame events
     * @param {string} gameName - The minigame name
     * @param {string} event - The event type
     * @param {Object} data - Game data
     */
    trackMinigame(gameName, event, data = {}) {
        if (!this.enabled) return

        this.track('Minigame Event', {
            game_name: gameName,
            game_event: event,
            ...data
        })
    }

    /**
     * Track player actions
     * @param {string} action - The player action
     * @param {Object} data - Action data
     */
    trackPlayerAction(action, data = {}) {
        if (!this.enabled) return

        this.track('Player Action', {
            player_action: action,
            ...data
        })
    }

    /**
     * Set user properties
     * @param {Object} user - User data
     */
    setUserProfile(user) {
        if (!this.canTrack() || !user.id) return

        try {
            this.mixpanelApi?.identify?.(user.id.toString())
            
            this.mixpanelApi?.people?.set?.({
                $name: user.username,
                username: user.username,
                member: user.member || false,
                rank: user.rank || null,
                coins: user.coins || 0,
                color: user.color,
                last_seen: new Date().toISOString()
            })
        } catch (error) {
            console.error('[Mixpanel Client] Error setting user profile:', error)
        }
    }

    /**
     * Track user login
     * @param {Object} user - User data
     */
    trackLogin(user) {
        if (!this.canTrack()) return

        // Identify the user for proper event attribution
        if (user.id) {
            try {
                this.mixpanelApi?.identify?.(user.id.toString())
            } catch (error) {
                console.error('[Mixpanel Client] Error identifying user:', error)
            }
        }

        this.track('Client Login', {
            user_id: user.id,
            username: user.username,
            member: user.member || false,
            login_method: 'client'
        })

        this.setUserProfile(user)
    }

    /**
     * Track user logout
     */
    trackLogout() {
        if (!this.canTrack()) return

        this.track('Client Logout', {
            logout_method: 'client'
        })
    }

    /**
     * Get current scene name from Phaser
     * @returns {string} Current scene name
     */
    getCurrentScene() {
        try {
            if (window.game && window.game.scene) {
                const activeScenes = window.game.scene.getScenes(true)
                return activeScenes.length > 0 ? activeScenes[0].scene.key : 'unknown'
            }
        } catch (error) {
            // Silent error handling
        }
        return 'unknown'
    }

    /**
     * Track room join on client
     * @param {number} roomId - Room ID
     * @param {string} roomName - Room name
     */
    trackRoomJoin(roomId, roomName) {
        if (!this.enabled) return

        this.track('Client Room Join', {
            room_id: roomId,
            room_name: roomName,
            scene: this.getCurrentScene()
        })
    }

    /**
     * Track clothing/item equip
     * @param {string} itemType - Type of item
     * @param {number} itemId - Item ID
     */
    trackItemEquip(itemType, itemId) {
        if (!this.enabled) return

        this.track('Item Equipped', {
            item_type: itemType,
            item_id: itemId
        })
    }

    /**
     * Track emote usage
     * @param {number} emoteId - Emote ID
     */
    trackEmote(emoteId) {
        if (!this.enabled) return

        this.track('Client Emote', {
            emote_id: emoteId
        })
    }

    /**
     * Track chat message sent
     * @param {string} messageType - Type of message
     * @param {number} messageLength - Length of message
     */
    trackChatMessage(messageType, messageLength) {
        if (!this.enabled) return

        this.track('Client Chat Message', {
            message_type: messageType,
            message_length: messageLength
        })
    }

    /**
     * Track client-side purchases
     * @param {string} itemType - Type of item purchased
     * @param {number} itemId - Item ID
     * @param {number} cost - Item cost
     */
    trackPurchase(itemType, itemId, cost) {
        if (!this.enabled) return

        this.track('Client Purchase', {
            item_type: itemType,
            item_id: itemId,
            item_cost: cost
        })
    }

    /**
     * Track page load performance
     */
    trackPageLoad() {
        if (!this.enabled) return

        try {
            const navigation = performance.getEntriesByType('navigation')[0]
            if (navigation) {
                this.track('Page Load', {
                    load_time: navigation.loadEventEnd - navigation.loadEventStart,
                    dom_content_loaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
                    first_paint: performance.getEntriesByName('first-paint')[0]?.startTime || null,
                    first_contentful_paint: performance.getEntriesByName('first-contentful-paint')[0]?.startTime || null
                })
            }
        } catch (error) {
            console.error('[Mixpanel Client] Error tracking page load:', error)
        }
    }
}